/**
 * Everyone who eats here, accounts and children alike, in one list.
 *
 * Two sources have to be reconciled. The adults come from the household roster
 * (`households/{hid}.members`, keyed by uid); the children come from the
 * eaters subcollection. This hook is the only place that merge happens, so the
 * roster screen and the verdict sheet can never disagree about who is at the
 * table or what letter is on someone's avatar.
 *
 * Names are ALWAYS viewer-relative: whoever is looking is `selfLabel`, never
 * their own first name. The design prototype shipped the opposite bug — played
 * as Alex, it rendered "Toi cuisine" on Camille's meals — and the handoff calls
 * it out explicitly. Initials are still computed from real names, so "Camille
 * (toi)" keeps her C.
 */
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { useHouseholdMembers } from './useHouseholdMembers';
import { initialsFor, nameFor } from '../services/householdService';
import { subscribeToEaters, type Eater } from '../services/eatersService';

export interface TableMember {
  /** uid for account holders, generated eater id for everyone else. */
  id: string;
  /** What to call them on screen — already viewer-relative. */
  name: string;
  initial: string;
  /** True for the two Firebase accounts; false for a dictated eater. */
  isAccount: boolean;
  isSelf: boolean;
}

export interface HouseholdEaters {
  /** Viewer first, then the partner, then eaters oldest-first. */
  table: TableMember[];
  /** The non-account entries only — what the roster screen lets you edit. */
  eaters: Eater[];
  loading: boolean;
  myUid: string | null;
  householdId: string | null;
}

/**
 * One Firestore listener per scope, shared by every mounted hook.
 *
 * VerdictAvatars renders on every cooked card, so a per-instance subscription
 * would open one listener per card — seven on a week grid, plus the sheet.
 * Ref-counting collapses them to one and replays the last snapshot to late
 * subscribers so a card mounting mid-session still paints immediately.
 */
interface EatersEntry {
  subscribers: Set<(list: Eater[]) => void>;
  unsubscribe: () => void;
  last: Eater[] | null;
}

const eatersCache = new Map<string, EatersEntry>();

const subscribeShared = (
  myUid: string,
  householdId: string | null,
  cb: (list: Eater[]) => void
): (() => void) => {
  const key = `${myUid}:${householdId ?? ''}`;
  let entry = eatersCache.get(key);
  if (!entry) {
    const created: EatersEntry = {
      subscribers: new Set(),
      unsubscribe: () => {},
      last: null,
    };
    eatersCache.set(key, created);
    created.unsubscribe = subscribeToEaters(myUid, householdId, (list) => {
      created.last = list;
      created.subscribers.forEach((s) => s(list));
    });
    entry = created;
  }
  entry.subscribers.add(cb);
  if (entry.last) cb(entry.last);
  return () => {
    const live = eatersCache.get(key);
    if (!live) return;
    live.subscribers.delete(cb);
    if (live.subscribers.size === 0) {
      live.unsubscribe();
      eatersCache.delete(key);
    }
  };
};

export const useHouseholdEaters = (selfLabel: string): HouseholdEaters => {
  const household = useHouseholdMembers();
  const authUser = useSelector((s: RootState) => s.auth?.user ?? null);
  const householdId = useSelector((s: RootState) => s.auth?.householdId ?? null);
  const myUid = household.myUid ?? authUser?.uid ?? null;

  const [eaters, setEaters] = useState<Eater[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!myUid) {
      setEaters([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    return subscribeShared(myUid, householdId, (list) => {
      setEaters(list);
      setLoading(false);
    });
  }, [myUid, householdId]);

  const table = useMemo<TableMember[]>(() => {
    if (!myUid) return [];

    // A solo user is not in any roster, so fall back to their auth profile
    // rather than rendering the person holding the phone as '?'.
    const myRealName =
      household.myName ??
      nameFor({
        displayName: authUser?.displayName ?? null,
        email: authUser?.email ?? null,
        updatedAt: '',
      }) ??
      null;

    const accounts: Array<{ id: string; realName: string | null; isSelf: boolean }> = [
      { id: myUid, realName: myRealName, isSelf: true },
    ];
    if (household.partnerUid) {
      accounts.push({
        id: household.partnerUid,
        realName: household.partnerName,
        isSelf: false,
      });
    }

    const rows = [
      ...accounts.map((a) => ({ ...a, isAccount: true })),
      ...eaters.map((e) => ({
        id: e.id,
        realName: e.name,
        isSelf: false,
        isAccount: false,
      })),
    ];

    // Disambiguate across the WHOLE table, not just the two adults — a child
    // sharing a first letter with a parent is the common case, not the edge.
    const initials = initialsFor(rows.map((r) => r.realName));

    return rows.map((r, i) => ({
      id: r.id,
      name: r.isSelf ? selfLabel : r.realName || '?',
      initial: initials[i],
      isAccount: r.isAccount,
      isSelf: r.isSelf,
    }));
  }, [
    myUid,
    household.myName,
    household.partnerUid,
    household.partnerName,
    authUser?.displayName,
    authUser?.email,
    eaters,
    selfLabel,
  ]);

  return { table, eaters, loading: loading || household.loading, myUid, householdId };
};
